import {
  Alert,
  Avatar,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import API from '../../../../services/API'
import DetailsBox, { DetailRow } from '../../../../components/Cards/DetailsBox'
import moment from 'moment'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import TripleCheck from '../../../../components/Switch/TripleCheck'
import { GlobalContext } from '../../../../App'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

const AuditPage = () => {
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [audit, setAudit] = useState(null)
  const { auditID } = useParams()
  const [newReviewNote, setNewReviewNote] = useState('')
  const [discrepancyRecord, setDiscrepancyRecord] = useState(null)
  const { setToast } = useContext(GlobalContext)
  const {setPageTitle} = useDocumentTitle()

  const loadAudit = () => {
    setLoading(true)
    API.get(`/medsmx/audit/${auditID}`)
      .then((res) => {
        if (res.status === 200) {
          setAudit(res.data)
        }
      })
      .catch((e) => {
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  const handleSubmitNewReview = () => {
    API.post('/medsmx/audit/review', {
      newReviewNote,
      auditID: audit._id,
    }).then((res) => {
      if (res.status === 200) {
        loadAudit()
        setToast({ message: 'Audit Reviewed' })
      }
    })
  }

  useEffect(() => {
    loadAudit()
    // Governance-only; a 403 simply leaves the banner off.
    API.get(`/medsmx/discrepancies?audit=${auditID}`).then((res) => {
      if (res.status === 200) setDiscrepancyRecord(res.data[0] ?? null)
    })
  }, [auditID])

  useEffect(() => {
    if (audit?.location?.name) {
      setPageTitle(audit.location.name + ' Audit')
    }
  }, [audit?.location?.name, setPageTitle])

  return (
    <Stack gap={1}>
      <Typography variant="title2">Medicine Audit</Typography>

      {loading && <LinearProgress />}
      {fetchError && (
        <Alert severity="error">
          Unable to fetch Audit. Please try again later.
        </Alert>
      )}

      {audit && (
        <>
          <Box component={Paper} padding={1}>
            <DetailsBox
              title="Audit Details"
              rows={[
                {
                  label: 'Audited By',
                  value: (
                    <Link
                      to={`/employees/${audit.completedBy._id}`}
                      target="_blank"
                      sx={{ textDecoration: 'none' }}
                    >
                      {audit.completedBy.firstName +
                        ' ' +
                        audit.completedBy.lastName}
                    </Link>
                  ),
                },
                {
                  label: 'Date',
                  value: moment(audit.createdAt).format('DD/MM/YYYY HH:mm'),
                },
                {
                  label: 'Bag',
                  value: (
                    <Link
                      to={`/medicines-management/${audit.location._id}`}
                      target="_blank"
                      sx={{ textDecoration: 'none' }}
                    >{`${audit.location.name}`}</Link>
                  ),
                },
                {
                  label: 'Bag Opened',
                  value: audit.bagOpened ? 'Yes' : 'No',
                },
                {
                  label: 'Original Tag',
                  value:
                    audit.originalTag === 'Removed'
                      ? 'Not Tagged'
                      : `${audit.originalTag}${
                          audit.originalTagScanned ? ' (verified by scan)' : ''
                        }`,
                },
                {
                  label: 'New Tag',
                  value:
                    audit.newTag === 'Removed'
                      ? 'Not Tagged'
                      : `${audit.newTag}${
                          audit.newTagScanned ? ' (recorded by scan)' : ''
                        }`,
                },
              ]}
            />
          </Box>
          <Alert
            severity={audit.discrepancies ? 'error' : 'success'}
            variant="filled"
          >
            {audit.discrepancies
              ? `There were discrepancies in this audit. ${
                  !audit.reviewedBy
                    ? 'Please review them below.'
                    : 'These have been marked as reviewed.'
                }`
              : 'No discrepancies found'}
          </Alert>
          {discrepancyRecord && discrepancyRecord.status !== 'Resolved' && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  component={Link}
                  to={`/medicines-management/discrepancies/${discrepancyRecord._id}`}
                >
                  Open Discrepancy
                </Button>
              }
            >
              The discrepancy record for this audit is{' '}
              {discrepancyRecord.status === 'Under Investigation'
                ? 'under investigation'
                : 'open'}{' '}
              and has not been resolved.
            </Alert>
          )}
          {audit.incidentDecision === 'created' && audit.incident && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  component={Link}
                  to={`/incidents/${audit.incident._id}`}
                >
                  View Incident
                </Button>
              }
            >
              Incident #{audit.incident.incidentNumber} was raised for the
              discrepancies found in this audit.
            </Alert>
          )}
          {audit.incidentDecision === 'declined' && (
            <Alert severity="info">
              The auditor chose not to raise an incident for the discrepancies
              found in this audit.
            </Alert>
          )}
          {audit.stockUpdated && (
            <Box
              component={Paper}
              padding={2}
              sx={{ borderLeft: 'solid 4px', borderLeftColor: 'anPurple.main' }}
            >
              <Typography variant="title3" mb={1}>
                Bag Contents Updated
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                The bag&apos;s recorded contents were updated as part of this
                audit:
              </Typography>
              <Stack spacing={0.5}>
                {(audit.stockChanges?.removals ?? []).map((change, index) => (
                  <Typography key={`removal-${index}`}>
                    • Removed {change.qty} × <strong>{change.title}</strong>{' '}
                    (batch {change.batch})
                    {change.requestedQty > change.qty &&
                      ` — ${change.requestedQty} were missing but only ${change.qty} existed in the stock records`}
                  </Typography>
                ))}
                {(audit.stockChanges?.additions ?? []).map((change, index) => (
                  <Typography key={`addition-${index}`}>
                    • Added {change.qty} × <strong>{change.title}</strong>{' '}
                    (batch {change.batch}, expires{' '}
                    {moment(change.expiry).format('DD/MM/YYYY')})
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
          <CustomDataGrid
            rows={audit.medicines ?? []}
            columns={[
              {
                field: 'correct',
                renderCell: ({ row }) => (
                  <Box
                    display="flex"
                    height="100%"
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <TripleCheck value={row.isCorrect} disableOnChange />
                  </Box>
                ),
                headerName: '',
              },
              {
                field: 'quantity',
                headerName: 'QTY',
                minWidth: 100,
                renderCell: ({ row }) => (
                  <Typography>{row.quantity}</Typography>
                ),
              },
              {
                field: 'actualQuantity',
                headerName: 'Actual QTY',
                minWidth: 120,
                renderCell: ({ row }) => (
                  <Typography
                    color={row.quantity !== row.actualQuantity && 'error'}
                    fontWeight={row.quantity !== row.actualQuantity && 'bold'}
                  >
                    {row.actualQuantity !== undefined
                      ? row.actualQuantity
                      : '—'}
                  </Typography>
                ),
              },
              {
                field: 'title',
                headerName: 'Drug',
                minWidth: 200,
                flex: 1,
              },
              { field: 'batch', headerName: 'Batch', minWidth: 140 },
              {
                field: 'expiry',
                headerName: 'Expiry',
                minWidth: 140,
                renderCell: ({ row }) =>
                  row?.expiry && moment(row.expiry).format('DD/MM/YYYY'),
              },
              {
                field: 'constitution',
                headerName: 'Constitution',
                minWidth: 120,
              },
              {
                field: 'presentation',
                headerName: 'Presentation',
                minWidth: 120,
              },
            ]}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            getRowId={(row) => row.uniqueID}
            slots={{
              footer: () => null,
              toolbar: () => null,
            }}
          />
          {audit.discrepancies &&
            audit.medicines.some((m) => m.isCorrect === false) && (
              <Box
                mt={4}
                p={2}
                sx={{
                  backgroundColor: '#f9f9fb',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Discrepancy Details
                </Typography>
                <Stack spacing={1}>
                  {audit.medicines
                    .filter((row) => row.isCorrect === false)
                    .map((row, index) => (
                      <Box
                        key={row._id + row.batch + index}
                        display="flex"
                        gap={2}
                        alignItems="center"
                        sx={{
                          backgroundColor: '#ffffff',
                          p: 1.5,
                          borderRadius: 1,
                          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: '#f0f0f5',
                          },
                        }}
                      >
                        <Typography flex={1}>
                          <strong>{row.title}</strong> ({row.batch}) — Expected:{' '}
                          {row.quantity}, Found: {row.actualQuantity ?? '—'}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </Box>
            )}
          <Box padding={1} component={Paper}>
            <DetailsBox title="Audit Review">
              {audit.reviewedBy ? (
                <>
                  <DetailRow
                    label="Reviewed By"
                    value={
                      audit.reviewedBy.firstName +
                      ' ' +
                      audit.reviewedBy.lastName
                    }
                  />
                  <DetailRow
                    label="Date"
                    value={moment(audit.reviewedDate).format(
                      'DD/MM/YYYY HH:mm'
                    )}
                  />
                  <DetailRow
                    label="Notes"
                    value={audit.reviewNotes}
                    singleColumn
                  />
                </>
              ) : (
                <Stack gap={1}>
                  <TextField
                    value={newReviewNote}
                    onChange={(e) => setNewReviewNote(e.target.value)}
                    label="Review Note"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <Button
                    fullWidth
                    color="success"
                    variant="contained"
                    disabled={!newReviewNote}
                    onClick={handleSubmitNewReview}
                  >
                    Submit
                  </Button>
                </Stack>
              )}
            </DetailsBox>
          </Box>
        </>
      )}
    </Stack>
  )
}

export default AuditPage
